/**
 * Payment form component
 * Handles payment submission with validation
 */

import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useSaveUserPaymentData, useUpdateUserPaymentData } from '@/hooks/useUserPaymentData'
import { MOBILE_NETWORKS, MIN_AMOUNT, MAX_AMOUNT, PHONE_REGEX } from '@/utils/constants'
import { formatCurrency, convertToApiPhoneFormat, normalizePhoneNumber } from '@/utils/format'
import { validateQRPayment, processQRPayment } from '@/services/api'
import type { MobileNetwork, MobileMoneyAccount, UserPaymentData, QRPaymentPayload } from '@/types'
import { MobileMoneyDropdown } from './MobileMoneyDropdown'
import toast from 'react-hot-toast'

// Form validation schema
const paymentSchema = z.object({
  amount: z
    .number({ required_error: 'Amount is required' })
    .min(MIN_AMOUNT, `Amount must be at least ${formatCurrency(MIN_AMOUNT)}`)
    .max(MAX_AMOUNT, `Amount must not exceed ${formatCurrency(MAX_AMOUNT)}`),
  phone: z
    .string({ required_error: 'Phone number is required' })
    .regex(PHONE_REGEX, 'Please enter a valid 10-digit phone number (e.g., 0244123456)'),
  network: z.enum(['MTN', 'TELECEL', 'AIRTELTIGO'], {
    required_error: 'Please select a mobile network',
  }),
})

type PaymentFormData = z.infer<typeof paymentSchema>

interface PaymentFormProps {
  merchantCode: string
  deviceTag: string
  userPaymentData?: UserPaymentData | null
  onSuccess?: () => void
}

export function PaymentForm({
  merchantCode,
  deviceTag,
  userPaymentData,
  onSuccess,
}: PaymentFormProps) {
  // Debug: Log userPaymentData to see what we're receiving
  console.log('[PaymentForm] Received userPaymentData:', userPaymentData)
  console.log('[PaymentForm] userPaymentData.id:', userPaymentData?.id)

  // Use last_used as the default selected account
  const [selectedAccount, setSelectedAccount] = useState<MobileMoneyAccount | null>(
    userPaymentData?.last_used || null
  )

  // Track the previous last_used.id to detect when it actually changes
  const previousLastUsedIdRef = useRef<number | null>(userPaymentData?.last_used?.id || null)

  // State for managing add new mobile money form
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<PaymentFormData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      phone: userPaymentData?.last_used
        ? normalizePhoneNumber(userPaymentData.last_used.mobile_number)
        : '',
      network: (userPaymentData?.last_used?.operator as MobileNetwork) || 'MTN',
      amount: undefined,
    },
  })

  // Update selected account when userPaymentData changes (e.g., after adding new account)
  useEffect(() => {
    const currentLastUsedId = userPaymentData?.last_used?.id || null

    // Only update selectedAccount if:
    // 1. selectedAccount is null (initial state)
    // 2. OR last_used.id actually changed (new account was added and became last_used)
    if (userPaymentData?.last_used && !selectedAccount) {
      // Initial state - set to last_used
      console.log('[PaymentForm] Setting initial selectedAccount to last_used')
      setSelectedAccount(userPaymentData.last_used)
      previousLastUsedIdRef.current = currentLastUsedId
    } else if (currentLastUsedId && previousLastUsedIdRef.current !== currentLastUsedId && userPaymentData?.last_used) {
      // last_used.id changed (new account added) - update to the new last_used
      console.log('[PaymentForm] last_used changed, updating selectedAccount to new last_used')
      setSelectedAccount(userPaymentData.last_used)
      previousLastUsedIdRef.current = currentLastUsedId
    }
    // If user manually selected a different account, don't override it
  }, [userPaymentData?.last_used, selectedAccount])

  // Update form when selected account changes
  useEffect(() => {
    if (selectedAccount) {
      setValue('phone', normalizePhoneNumber(selectedAccount.mobile_number))
      setValue('network', selectedAccount.operator as MobileNetwork)
    }
  }, [selectedAccount, setValue])

  const saveUserPaymentData = useSaveUserPaymentData()
  const updateUserPaymentData = useUpdateUserPaymentData()
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false)

  const watchedAmount = watch('amount')
  const hasSavedAccounts = userPaymentData?.mobile_money_accounts && userPaymentData.mobile_money_accounts.length > 0

  const handleAccountSelect = (account: MobileMoneyAccount) => {
    setSelectedAccount(account)
  }

  const handleAddNewMobileMoney = async (data: { operator: string; mobile_number: string }) => {
    try {
      console.log('[handleAddNewMobileMoney] === FUNCTION CALLED ===')
      console.log('[handleAddNewMobileMoney] Input data:', data)
      console.log('[handleAddNewMobileMoney] userPaymentData:', userPaymentData)
      console.log('[handleAddNewMobileMoney] userPaymentData?.id:', userPaymentData?.id)
      console.log('[handleAddNewMobileMoney] hasUserId:', !!userPaymentData?.id)
      console.log('[handleAddNewMobileMoney] deviceTag:', deviceTag)

      if (userPaymentData?.id) {
        console.log('[handleAddNewMobileMoney] ✅ TAKING PATCH BRANCH - User exists with ID:', userPaymentData.id)
        const payload = {
          userId: userPaymentData.id,
          tag: deviceTag,
          mobile_money: data,
        }
        console.log('[handleAddNewMobileMoney] PATCH payload:', payload)
        console.log('[handleAddNewMobileMoney] Calling updateUserPaymentData.mutateAsync...')

        const result = await updateUserPaymentData.mutateAsync(payload)

        console.log('[handleAddNewMobileMoney] ✅ PATCH successful, result:', result)
        toast.success('Mobile money number added successfully!')
      } else {
        console.log('[handleAddNewMobileMoney] ⚠️ TAKING POST BRANCH - No user ID found')
        console.log('[handleAddNewMobileMoney] userPaymentData was:', userPaymentData)
        const payload = {
          tag: deviceTag,
          mobile_money: data,
        }
        console.log('[handleAddNewMobileMoney] POST payload:', payload)
        console.log('[handleAddNewMobileMoney] Calling saveUserPaymentData.mutateAsync...')

        const result = await saveUserPaymentData.mutateAsync(payload)

        console.log('[handleAddNewMobileMoney] ✅ POST successful, result:', result)
        toast.success('Mobile money number added successfully!')
      }
    } catch (error) {
      console.error('[handleAddNewMobileMoney] ❌ ERROR:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      toast.error(`Failed to add mobile money number: ${errorMessage}`)
      throw error // Re-throw so the form can handle it
    }
  }

  const onSubmit = async (data: PaymentFormData) => {
    setIsPaymentProcessing(true)
    try {
      console.log('[PaymentForm] Payment submission started')

      // Use selected account data if available, otherwise use form data
      const phoneNumber = selectedAccount
        ? normalizePhoneNumber(selectedAccount.mobile_number)
        : data.phone
      const network = selectedAccount ? (selectedAccount.operator as MobileNetwork) : data.network
      const mobileNumber = convertToApiPhoneFormat(phoneNumber)

      console.log('[PaymentForm] Payment details:', {
        phoneNumber,
        network,
        mobileNumber,
        amount: data.amount,
      })

      // Step 1: Save user payment data first (only if not using existing account or if it's a new number)
      if (!selectedAccount || (selectedAccount.mobile_number !== mobileNumber)) {
        try {
          console.log('[PaymentForm] Saving user payment data...')
          await saveUserPaymentData.mutateAsync({
            tag: deviceTag,
            mobile_money: {
              operator: network,
              mobile_number: mobileNumber,
            },
          })
          console.log('[PaymentForm] User payment data saved')
        } catch (error) {
          // Log error but don't block payment if user tag save fails
          console.warn('[PaymentForm] Failed to save user payment data:', error)
          // Still proceed with payment
        }
      }

      // Step 2: Build QR payment payload
      const paymentPayload: QRPaymentPayload = {
        payment_to: 'merchant',
        send_amount: data.amount,
        payer_account_details: {
          currency: 'GHS',
          country: 'GH',
          account_type: 'mobile_money',
          mobile_money: {
            mobile_number: mobileNumber,
            operator: network,
          },
        },
        guest_first_name: userPaymentData?.owner_name || '',
        sender_currency: 'GHS',
        receiver_currency: 'GHS',
        merchant_id: merchantCode,
      }

      console.log('[PaymentForm] Payment payload:', paymentPayload)

      // Step 3: Validate payment
      console.log('[PaymentForm] Validating payment...')
      toast.loading('Validating payment...', { id: 'payment-validation' })
      await validateQRPayment(paymentPayload)
      toast.dismiss('payment-validation')
      console.log('[PaymentForm] Payment validation successful')

      // Step 4: Process payment
      console.log('[PaymentForm] Processing payment...')
      toast.loading('Processing payment...', { id: 'payment-processing' })
      const response = await processQRPayment(paymentPayload)
      toast.dismiss('payment-processing')

      console.log('[PaymentForm] Payment processed successfully:', response)

      // Show success message
      toast.success(`Payment submitted successfully! Status: ${response.status}`)

      // Call onSuccess callback
      onSuccess?.()
    } catch (error) {
      toast.dismiss('payment-validation')
      toast.dismiss('payment-processing')

      console.error('[PaymentForm] Payment error:', error)
      const errorMessage = error instanceof Error ? error.message : 'An error occurred. Please try again.'
      toast.error(errorMessage)
    } finally {
      setIsPaymentProcessing(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Amount Input - Biggest Element */}
      <div>
        <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-3">
          Amount (GHS)
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">₵</span>
          <input
            id="amount"
            type="number"
            step="0.01"
            min={MIN_AMOUNT}
            max={MAX_AMOUNT}
            {...register('amount', { valueAsNumber: true })}
            className={`w-full pl-12 pr-4 py-5 text-2xl border-2 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.amount ? 'border-error-500' : 'border-gray-300'
            }`}
            placeholder="0.00"
          />
        </div>
        {errors.amount && (
          <p className="mt-2 text-sm text-error-600">{errors.amount.message}</p>
        )}
        {watchedAmount && !errors.amount && (
          <p className="mt-2 text-sm text-gray-500">{formatCurrency(watchedAmount)}</p>
        )}
      </div>

      {/* Mobile Money Account Selection or Manual Entry */}
      {hasSavedAccounts ? (
        <>
          {/* From Label and Add New Number Button on same line */}
          <div className="flex justify-between items-end mb-2">
            <label className="block text-sm font-medium text-gray-700">From</label>
            <button
              type="button"
              onClick={() => {
                setIsDropdownOpen(true)
                setShowAddForm(true)
              }}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg border-2 border-dashed border-gray-300 bg-white hover:border-primary-500 hover:bg-primary-50 transition-all text-sm text-gray-600"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              <span>Add New Number</span>
            </button>
          </div>

          <MobileMoneyDropdown
            accounts={userPaymentData.mobile_money_accounts}
            lastUsed={userPaymentData.last_used}
            selectedAccount={selectedAccount}
            onSelect={handleAccountSelect}
            onAddNew={handleAddNewMobileMoney}
            isLoading={updateUserPaymentData.isPending || saveUserPaymentData.isPending}
            isOpen={isDropdownOpen}
            setIsOpen={setIsDropdownOpen}
            showAddForm={showAddForm}
            setShowAddForm={setShowAddForm}
          />
        </>
      ) : (
        <>
          {/* Phone Number Input */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              Mobile Number
            </label>
            <input
              id="phone"
              type="tel"
              {...register('phone')}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.phone ? 'border-error-500' : 'border-gray-300'
              }`}
              placeholder="0244123456"
              maxLength={10}
            />
            {errors.phone && <p className="mt-1 text-sm text-error-600">{errors.phone.message}</p>}
          </div>

          {/* Network Selection */}
          <div>
            <label htmlFor="network" className="block text-sm font-medium text-gray-700 mb-2">
              Mobile Network
            </label>
            <select
              id="network"
              {...register('network')}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.network ? 'border-error-500' : 'border-gray-300'
              }`}
            >
              {MOBILE_NETWORKS.map((network) => (
                <option key={network.value} value={network.value}>
                  {network.label}
                </option>
              ))}
            </select>
            {errors.network && <p className="mt-1 text-sm text-error-600">{errors.network.message}</p>}
          </div>
        </>
      )}


      {/* Submit Button - Dark pill-shaped button */}
      <button
        type="submit"
        disabled={isSubmitting || isPaymentProcessing || saveUserPaymentData.isPending || updateUserPaymentData.isPending}
        className="w-full bg-gray-900 text-white py-4 px-6 rounded-full font-semibold text-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isSubmitting || isPaymentProcessing || saveUserPaymentData.isPending || updateUserPaymentData.isPending ? (
          <span className="flex items-center justify-center">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Processing...
          </span>
        ) : (
          'Pay'
        )}
      </button>
    </form>
  )
}
